import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES, ROLE_HOME } from '@/config/routes'
import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/config/supabase'

/**
 * ProtectedRoute — wraps routes that require auth and/or a specific role.
 *
 * Usage in App.jsx:
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/book" element={<BookingEntry />} />
 *   </Route>
 *
 *   <Route element={<ProtectedRoute requiredRole="admin" />}>
 *     <Route path="/admin" element={<Dashboard />} />
 *   </Route>
 */

const Spinner = () => (
  <div className="min-h-dvh flex items-center justify-center bg-surface-base">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 bg-siglat-teal/15 rounded-lg flex items-center justify-center">
        <span className="text-siglat-teal text-sm font-black">SK</span>
      </div>
      <Loader2 className="w-5 h-5 text-siglat-teal animate-spin" />
    </div>
  </div>
)

export default function ProtectedRoute({ requiredRole }) {
  const { user, profile, loading } = useAuth()
  const location = useLocation()

  // Grace period: when user suddenly becomes null, wait 2.5s before redirecting.
  // This prevents false logouts from Supabase's tab-switch SIGNED_OUT event.
  // If the session recovers within 2.5s (via useSessionPersistence), we stay put.
  const [grace, setGrace]   = useState(false)
  const graceTimer          = useRef(null)
  const prevUser            = useRef(user)

  useEffect(() => {
    const hadUser = prevUser.current
    prevUser.current = user

    if (hadUser && !user && !loading) {
      // User just disappeared — start grace period, verify with Supabase directly
      setGrace(true)
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          // Real session still exists — false logout, stay put
          setGrace(false)
        } else {
          // No session — start 2.5s countdown before redirecting
          graceTimer.current = setTimeout(() => setGrace(false), 2500)
        }
      })
    } else if (user) {
      // User restored — cancel any pending grace timer
      setGrace(false)
      if (graceTimer.current) clearTimeout(graceTimer.current)
    }

    return () => { if (graceTimer.current) clearTimeout(graceTimer.current) }
  }, [user, loading])

  // ── 1. Still checking session ─────────────────────────────────────────────
  if (loading || grace) return <Spinner />

  // ── 2. Not logged in → redirect to /login ─────────────────────────────────
  if (!user) {
    return (
      <Navigate
        to={ROUTES.LOGIN}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  // ── 3. Logged in but no profile yet → needs profile setup ─────────────────
  //    Profile is created by DB trigger on auth.users INSERT, but on first
  //    Google login the profile row may not exist until they complete setup.
  if (!profile && location.pathname !== '/setup-profile') {
    return <Navigate to="/setup-profile" replace />
  }

  // ── 4. Role mismatch → send to their correct home ─────────────────────────
  if (requiredRole && profile?.role !== requiredRole) {
    const home = ROLE_HOME[profile?.role] ?? ROUTES.LOGIN
    return <Navigate to={home} replace />
  }

  // ── 5. All checks passed ───────────────────────────────────────────────────
  return <Outlet />
}
